import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react'
import { Box, IconButton, CircularProgress, SvgIcon, Tooltip } from '@mui/material'

import CellLinkingMenu from './components/CellLinkingMenu'
import CellActionsMenu from './components/CellActionsMenu'
import { useUpdateTPPCellContentMutation } from './queries/useUpdateTPPCellContentMutation'
import { useDecloneTPPCellMutation } from './queries/useDecloneTPPCellMutation'
import { isCellLocked } from './utils/CellLockUtil'
import { ReactComponent as CloneActionIcon } from '../../../../assets/images/cloneto_icon.svg'
//import Linking from '../../../../components/Linking'
import TppColumnLockIcon from '../../../../components/TppView/components/TppColumnLockIcon'
import { useTheme } from '@mui/styles'
import TPPCellUI from './TPPCellUI'

export default function TPPCellCard({
    cell,
    dispatch,
    isSlotHovered,
    visibleColumns,
    columnsById,
    cellsById,
    occupiedColumnIds,
    isMovePending,
    onEditingChange,
    canEdit = true,
}: {
    cell: any
    dispatch: any
    isSlotHovered?: boolean
    visibleColumns: { id: string; label: string }[]
    columnsById: Record<string, any>
    cellsById: Record<string, any>
    occupiedColumnIds: string[]
    isMovePending?: boolean
    onEditingChange?: (editing: boolean) => void
    canEdit?: boolean
}) {
    const CONTENT_MAX_HEIGHT = 350
    const [isEditing, setIsEditing] = useState(false)
    const [draftContent, setDraftContent] = useState(cell?.content || '')
    const [isHovered, setIsHovered] = useState(false)
    const [isActionMenuOpen, setIsActionMenuOpen] = useState(false)
    const [justEdited, setJustEdited] = useState(false)
    const highlightTimeoutRef = useRef<number | null>(null)

    const { mutate: updateContent } = useUpdateTPPCellContentMutation()
    const { mutateAsync: decloneTPPCell, isLoading: isDecloning } = useDecloneTPPCellMutation()

    const locked = isCellLocked(cell?.locked)
    const isColumnLocked = !!columnsById?.[cell.columnId]?.locked
    const isColumnArchived = !!columnsById?.[cell.columnId]?.archived
    const isCloneChild = !!cell?.isClone
    const isReadOnly = locked || isColumnLocked || isColumnArchived || isCloneChild || isMovePending || !canEdit

    const parentCell = useMemo(() => {
        if (!isCloneChild || !cellsById) return null
        const cells = Object.values(cellsById)
        return cells.find((c) => Array.isArray(c.childClones) && c.childClones.includes(cell._id)) || null
    }, [cellsById, cell?._id, isCloneChild])

    const fallbackColumnTitle = cell?.cloneSource ? columnsById?.[cell.cloneSource]?.title : null
    const parentColumnTitle = parentCell ? columnsById?.[parentCell.columnId]?.title : fallbackColumnTitle
    const displayContent = isCloneChild && parentColumnTitle ? `Same as ${parentColumnTitle}` : cell?.content
    const trimmedDisplayContent = (displayContent || '').trim()
    const hasDisplayContent = trimmedDisplayContent.length > 0
    const displayText = hasDisplayContent ? displayContent : 'Add text'

    useEffect(() => {
        if (!isEditing) {
            setDraftContent(cell?.content || '')
        }
    }, [cell?._id, cell?.content, isEditing])

    useEffect(() => {
        onEditingChange?.(isEditing)
    }, [isEditing, onEditingChange])

    useEffect(() => {
        if (isEditing) {
            setJustEdited(true)
            if (highlightTimeoutRef.current !== null) {
                window.clearTimeout(highlightTimeoutRef.current)
            }
            highlightTimeoutRef.current = window.setTimeout(() => {
                setJustEdited(false)
            }, 800)
        }
    }, [isEditing])

    useEffect(() => {
        return () => {
            if (highlightTimeoutRef.current !== null) {
                window.clearTimeout(highlightTimeoutRef.current)
            }
        }
    }, [])

    const commit = () => {
        const nextRaw = draftContent
        const prev = cell?.content || ''
        const next = nextRaw.trim()

        setIsEditing(false)

        if (!next) {
            setDraftContent(prev)
            return
        }

        // No-op if unchanged
        if (next === prev) return

        if (cell?._id) {
            updateContent({
                cellId: cell._id,
                tppId: cell.tppId,
                content: next,
            })
        }

        // Fallback: dispatch a generic action shape
        if (typeof dispatch === 'function') {
            dispatch({
                type: 'TPP_CELL_UPDATE_CONTENT',
                payload: { cellId: cell?._id, content: next },
            })
        }
    }

    const cancel = () => {
        setDraftContent(cell?.content || '')
        setIsEditing(false)
    }

    const theme = useTheme()

    const cellBackgroundColor = isSlotHovered
        ? theme.palette.figma.primary3
        : cell?.isHighlighted
          ? theme.palette.figma.primary3
          : theme.palette.figma.grayscale.tier7

    const footerLeft = (
        <Box
            sx={{
                width: 76,
                minWidth: 76,
                display: 'flex',
                alignItems: 'center',
            }}
        >
            {!isCloneChild && (
                <CellLinkingMenu
                    cell={cell}
                    isColumnLocked={isColumnLocked}
                    isColumnArchived={isColumnArchived}
                    canEdit={canEdit}
                />
            )}
        </Box>
    )

    const footerRight = isCloneChild ? (
        <Tooltip title="Declone" placement="top" arrow>
            <span>
                <IconButton
                    size="small"
                    onClick={async () => {
                        if (!cell?._id) return
                        await decloneTPPCell({ cellId: cell._id, tppId: cell.tppId })
                    }}
                    disabled={isDecloning || isColumnLocked || isColumnArchived || !canEdit}
                    sx={{
                        color: 'figma.grayscale.tier2',
                        '&:hover': { color: 'figma.primary' },
                    }}
                >
                    <SvgIcon
                        component={isDecloning ? () => <CircularProgress size={20} color="primary" /> : CloneActionIcon}
                        inheritViewBox
                        fontSize="small"
                        color="inherit"
                        sx={{ fontSize: '20px' }}
                    />
                </IconButton>
            </span>
        </Tooltip>
    ) : (
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
            {isHovered || isActionMenuOpen ? (
                <CellActionsMenu
                    cell={cell}
                    dispatch={dispatch}
                    visibleColumns={visibleColumns}
                    occupiedColumnIds={occupiedColumnIds}
                    isMovePending={isMovePending}
                    onlyComment={isColumnLocked || isColumnArchived || !canEdit}
                    onOpenChange={setIsActionMenuOpen}
                />
            ) : null}
        </Box>
    )

    return (
        <TPPCellUI
            isEditing={isEditing}
            draftContent={draftContent}
            onDraftChange={setDraftContent}
            onCommit={commit}
            onCancel={cancel}
            onEnterEdit={() => {
                if (isReadOnly) return
                setIsEditing(true)
            }}
            displayText={displayText}
            hasDisplayContent={hasDisplayContent}
            isReadOnly={isReadOnly}
            isMovePending={isMovePending}
            contentMaxHeight={CONTENT_MAX_HEIGHT}
            backgroundColor={cellBackgroundColor}
            borderColor={justEdited ? 'figma.primary' : 'transparent'}
            borderWidth={2}
            textColor="figma.grayscale.tier2"
            placeholderColor="figma.grayscale.tier4"
            contentOpacity={locked || isCloneChild || isMovePending ? 0.6 : 1}
            onMouseEnter={() => setIsHovered(true)}
            onMouseLeave={() => setIsHovered(false)}
            topRight={
                !!locked ? (
                    <Box sx={{ position: 'absolute', top: 20, right: 20, zIndex: 1 }}>
                        <TppColumnLockIcon locked={cell.locked} />
                    </Box>
                ) : null
            }
            footerLeft={footerLeft}
            footerRight={footerRight}
        />
    )
}
